import { useState, useEffect, useCallback } from "react";
import { Hotel, ChevronDown, Check, Plus, Settings2 } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../i18n/language-provider";
import type { PmsConnectionSummary } from "../types/config";
import { fetchPmsConnections, activatePmsConnection } from "../services/pmsConnectionsApi";
import { getConnectionLabel } from "./pms/connectionLabel";
import { runWhenUnblocked } from "../navigation/history";

interface PmsConnectionSwitcherProps {
  activeConnectionId: number | null;
  onConnectionChanged: () => void;
  onManageConnections: () => void;
  collapsed?: boolean;
  connectionVersion?: number;
}

export function PmsConnectionSwitcher({
  activeConnectionId,
  onConnectionChanged,
  onManageConnections,
  collapsed = false,
  connectionVersion = 0,
}: PmsConnectionSwitcherProps) {
  const { t } = useLanguage();
  const [connections, setConnections] = useState<PmsConnectionSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const loadConnections = useCallback(() => {
    fetchPmsConnections()
      .then(setConnections)
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Reload when the active connection or any connection-level change occurs
  useEffect(() => {
    loadConnections();
  }, [activeConnectionId, connectionVersion, loadConnections]);

  const activeConn = connections.find((c) => c.id === activeConnectionId);
  const displayName = activeConn
    ? getConnectionLabel(activeConn, t)
    : t.connections.noActiveConnection;

  const handleSwitch = (connId: number) => {
    if (connId === activeConnectionId) {
      setOpen(false);
      return;
    }
    // A switch remounts every property-scoped page, so a screen holding
    // unsaved work — the strategy editor — is asked first rather than emptied.
    runWhenUnblocked(() => {
      void switchTo(connId);
    });
  };

  const switchTo = async (connId: number) => {
    setSwitching(true);
    try {
      await activatePmsConnection(connId);
      setOpen(false);
      onConnectionChanged();
    } catch {
      // toast could be added here
    } finally {
      setSwitching(false);
    }
  };

  if (collapsed) {
    return (
      <div className="py-2 flex flex-col items-center border-b border-sidebar-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(!open)}
          title={displayName}
          className="text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <Hotel className="h-4 w-4" />
        </Button>
        {open && (
          <div className="absolute left-16 top-auto z-50 bg-popover border border-border rounded-md shadow-lg py-1 min-w-48">
            {/* A revenue manager keeps every client in one account, and an
                administrator may hold read-only grants on many properties, so
                the list scrolls inside a bounded box rather than running off
                the bottom of the screen with the way to manage them under it. */}
            <div className="max-h-[min(18rem,50dvh)] overflow-y-auto overflow-x-hidden">
              {connections.map((conn) => (
                <button
                  key={conn.id}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2 disabled:opacity-50"
                  onClick={() => handleSwitch(conn.id)}
                  disabled={switching}
                >
                  {conn.id === activeConnectionId && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                  <span className={conn.id === activeConnectionId ? "font-medium" : ""}>
                    {getConnectionLabel(conn, t)}
                  </span>
                </button>
              ))}
            </div>
            <div className="border-t border-border mt-1 pt-1">
              <button
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2"
                onClick={onManageConnections}
              >
                <Settings2 className="h-3.5 w-3.5" />
                {t.connections.manage}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-sidebar-border">
      <button
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground text-sm transition-colors"
        onClick={() => setOpen(!open)}
      >
        <Hotel className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/70" />
        <span className="flex-1 text-left truncate">{displayName}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-1 bg-sidebar rounded-md border border-sidebar-border overflow-hidden">
          {/* Laid out inline in the rail, where nothing below it scrolls: a
              long list of properties would otherwise push the rest of the
              rail off the screen. */}
          <div className="max-h-[min(18rem,40dvh)] overflow-y-auto overflow-x-hidden touch-pan-y touch-pinch-zoom">
            {connections.map((conn) => (
              <button
                key={conn.id}
                className="w-full text-left px-3 py-2 text-sm hover:bg-sidebar-accent flex items-center gap-2 disabled:opacity-50 text-sidebar-foreground"
                onClick={() => handleSwitch(conn.id)}
                disabled={switching}
              >
                {conn.id === activeConnectionId ? (
                  <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                ) : (
                  <span className="w-3.5 flex-shrink-0" />
                )}
                <span className={`truncate ${conn.id === activeConnectionId ? "font-medium" : ""}`}>
                  {getConnectionLabel(conn, t)}
                </span>
              </button>
            ))}
          </div>
          <div className="border-t border-sidebar-border">
            <button
              className="w-full text-left px-3 py-2 text-sm hover:bg-sidebar-accent flex items-center gap-2 text-sidebar-foreground"
              onClick={() => {
                setOpen(false);
                onManageConnections();
              }}
            >
              <Plus className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{t.connections.manage}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
